import { track } from '@/lib/analytics/client';
import { useAuthModal } from 'modules/auth';
import Button from 'modules/common/components/Button';
import Hero from 'modules/common/components/Hero';
import Highlight from 'modules/common/components/Highlight';
import Section from 'modules/common/components/Section';
import StatsSection from 'modules/common/components/Stats';
import HeaderLayout from 'modules/common/layouts/HeaderLayout';
import type { NextPage } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaLinkedin } from 'react-icons/fa';
import { MdLocationOn, MdPhone } from 'react-icons/md';

type Ambassador = {
  _id: string;
  name: string;
  firstName: string;
  lastName: string;
  image?: string;
  title?: string;
  bio?: string;
  region?: string;
  businessName?: string;
  phone?: string;
  url?: string;
  ambassadorFor?: string;
};

const AmbassadorCard: React.FC<{ ambassador: Ambassador }> = ({ ambassador }) => {
  const fullName = ambassador.name;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden flex flex-col h-full">
      <div className="relative w-full" style={{ aspectRatio: '1/0.8' }}>
        {ambassador.image ? (
          <Image src={ambassador.image} alt={fullName} fill className="object-cover" />
        ) : (
          <div className="bg-gray-200 dark:bg-gray-700 h-full w-full flex items-center justify-center">
            <span className="text-3xl font-bold text-gray-400">{fullName}</span>
          </div>
        )}
      </div>
      <div className="p-4 flex-grow">
        <div className="flex items-center text-lg  dark:text-gray-400 mb-2">
          <MdLocationOn className="mr-1" />
          <span className="font-bold">{ambassador.ambassadorFor}</span>
        </div>
        <h3 className="text-sm font-bold mb-1">{fullName}</h3>
        <div className="flex mt-2 space-x-2">
          {ambassador.phone && (
            <a
              href={`tel:${ambassador.phone}`}
              className="btn flex items-center justify-center text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 transition-colors"
              onClick={() => track('ambassador.contacted-phone', { category: 'ambassadors', ambassadorId: ambassador._id, ambassadorName: fullName })}
            >
              <MdPhone className="text-base" />
              <span className="ml-1">Appeler</span>
            </a>
          )}
          {ambassador.url && (
            <a
              href={ambassador.url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn flex items-center justify-center  text-sm bg-[#0077b5] hover:bg-[#0066a1] text-white transition-colors"
              onClick={() =>
                track('ambassador.contacted-linkedin', { category: 'ambassadors', ambassadorId: ambassador._id, ambassadorName: fullName })
              }
            >
              <FaLinkedin className="text-base" />
              <span className="ml-1">LinkedIn</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const AmbassadorsPage: NextPage<{ ambassadors: Ambassador[] }> = ({ ambassadors = [] }) => {
  const { toggleModal } = useAuthModal();

  const ambassadorsByRegion = ambassadors.reduce((acc: Record<string, Ambassador[]>, ambassador) => {
    const region = ambassador.ambassadorFor || 'Non spécifié';
    if (!acc[region]) {
      acc[region] = [];
    }
    acc[region].push(ambassador);
    return acc;
  }, {} as Record<string, Ambassador[]>);

  const regions = Object.keys(ambassadorsByRegion);

  const items = [
    {
      value: '',
      label: '',
      moreInfo: '',
    },
    {
      value: '100%',
      label: 'Validés',
      moreInfo: 'par Headlinker',
    },
    {
      value: ambassadors.length.toString(),
      label: 'Ambassadeurs',
      moreInfo: 'à travers le monde',
    },
    {
      value: regions.length.toString(),
      label: 'Régions',
      moreInfo: 'représentées',
      aosDelay: 100,
    },
  ];

  const handleSignIn = () => {
    toggleModal();
  };

  return (
    <HeaderLayout
      title="Ambassadeurs Headlinker"
      description="Découvre nos ambassadeurs Headlinker à travers le monde qui vont t’aider dans ta région"
    >
      <Hero
        title={
          <>
            Nos <Highlight>Ambassadeurs</Highlight> à travers le monde
          </>
        }
        description={<>Découvre nos ambassadeurs Headlinker qui vont t’aider dans ta région et répondre à tes questions sur notre plateforme</>}
        cta={
          <Button onClick={handleSignIn} className="btn-primary btn" badge>
            Je deviens un Headlinker
          </Button>
        }
      />
      <StatsSection items={items} className="!pb-0" />
      <Section title="Nos ambassadeurs par région" subtitle="Contacte l'ambassadeur de ta région pour en savoir plus sur Headlinker">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-6">
          {ambassadors.map((ambassador) => (
            <AmbassadorCard key={ambassador._id} ambassador={ambassador} />
          ))}
        </div>
      </Section>
      <Section
        title="Rejoins notre réseau d'ambassadeurs"
        subtitle="Tu souhaites devenir ambassadeur Headlinker dans ta région ? Contacte-nous pour en savoir plus"
        className="text-center"
        bgColor="bg-dark text-white"
      >
        <Link
          scroll={false}
          className="btn btn-accent mx-auto mt-5"
          href="mailto:hello@headlinker.com?subject=Devenir%20ambassadeur%20Headlinker"
          onClick={() => track('ambassador.become.clicked', { category: 'ambassadors', label: 'contact-ambassador' })}
        >
          Deviens ambassadeur
        </Link>
      </Section>
    </HeaderLayout>
  );
};

export default AmbassadorsPage;
